import React from 'react';
import { Card, Space, Typography, Alert, Divider, Tag, List, Empty } from 'antd';
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  BulbOutlined
} from '@ant-design/icons';
import { VerifyData, ValidationIssue, IssueSeverity } from '../types';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;

interface VerificationResultsPanelProps {
  data: VerifyData;
}

const SEVERITY_CONFIG: Record<IssueSeverity, { icon: React.ReactNode; color: string; label: string }> = {
  error: {
    icon: <CloseCircleTwoTone twoToneColor="#ff4d4f" />,
    color: 'error',
    label: 'Error'
  },
  warning: {
    icon: <WarningOutlined style={{ color: '#faad14' }} />,
    color: 'warning',
    label: 'Warning'
  },
  info: {
    icon: <InfoCircleOutlined style={{ color: '#1890ff' }} />,
    color: 'info',
    label: 'Info'
  }
};

const ValidationIssueCard: React.FC<{ issue: ValidationIssue }> = ({ issue }) => {
  const config = SEVERITY_CONFIG[issue.severity];

  return (
    <Card
      size="small"
      style={{
        marginBottom: '12px',
        borderRadius: '8px',
        border: `1px solid ${
          issue.severity === 'error' ? '#ffccc7' :
          issue.severity === 'warning' ? '#ffe58f' :
          '#91d5ff'
        }`,
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
        transition: 'transform var(--netorca-dur-mid) var(--netorca-ease-standard), box-shadow var(--netorca-dur-mid) var(--netorca-ease-standard)',
        background: issue.severity === 'error'
          ? 'linear-gradient(135deg, #fff2f0 0%, #ffffff 100%)'
          : issue.severity === 'warning'
          ? 'linear-gradient(135deg, #fffbe6 0%, #ffffff 100%)'
          : 'linear-gradient(135deg, #e6f7ff 0%, #ffffff 100%)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.05)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Space>
          {config.icon}
          <Tag color={config.color}>{config.label.toUpperCase()}</Tag>
          {issue.step_name && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Step: {issue.step_name}
              {issue.step_index !== undefined && ` (${issue.step_index})`}
            </Text>
          )}
        </Space>

        <div>
          <Text strong>{issue.message}</Text>
        </div>

        {issue.suggestion && (
          <Alert
            message="Suggestion"
            description={issue.suggestion}
            type="info"
            showIcon
            icon={<BulbOutlined />}
            style={{ fontSize: '12px' }}
          />
        )}
      </Space>
    </Card>
  );
};

export const VerificationResultsPanel: React.FC<VerificationResultsPanelProps> = ({ data }) => {
  // Debug logging
  console.log('[VerificationResultsPanel] Received data:', data);
  console.log('[VerificationResultsPanel] Data type:', typeof data);
  console.log('[VerificationResultsPanel] Data keys:', data ? Object.keys(data) : 'null');

  if (!data) {
    return (
      <Empty
        description="No verification data available"
        style={{ padding: '40px 0' }}
      />
    );
  }

  // Error boundary - check for required fields
  if (typeof data !== 'object') {
    return (
      <Alert
        message="Error Rendering Verification Data"
        description={`Expected object but received ${typeof data}. Data: ${JSON.stringify(data).slice(0, 200)}`}
        type="error"
        showIcon
      />
    );
  }

  if (data.is_valid === undefined) {
    return (
      <Alert
        message="Invalid Verification Data Format"
        description={`Missing required field 'is_valid'. Available fields: ${Object.keys(data).join(', ')}`}
        type="error"
        showIcon
      />
    );
  }

  const issues = data.issues_found || [];
  const errors = issues.filter(i => i.severity === 'error');
  const warnings = issues.filter(i => i.severity === 'warning');
  const infos = issues.filter(i => i.severity === 'info');

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* Compact Validation Status */}
      <Card
        style={{
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          border: '1px solid #f0f0f0'
        }}
        bodyStyle={{ padding: '16px' }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text strong style={{ fontSize: '14px', color: '#666' }}>Verification Result</Text>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space size="small">
              <Tag color={data.is_valid ? 'success' : 'error'}>
                {data.is_valid ? 'PASSED' : 'FAILED'}
              </Tag>
              {errors.length > 0 && (
                <Tag color="error">{errors.length} Error{errors.length !== 1 ? 's' : ''}</Tag>
              )}
              {warnings.length > 0 && (
                <Tag color="warning">{warnings.length} Warning{warnings.length !== 1 ? 's' : ''}</Tag>
              )}
              {infos.length > 0 && (
                <Tag color="info">{infos.length} Info</Tag>
              )}
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {moment(data.timestamp).format('YYYY-MM-DD HH:mm:ss')}
              </Text>
            </Space>
          </div>
        </Space>
      </Card>

      {/* Summary */}
      {data.summary && (
        <Alert
          message="Summary"
          description={data.summary}
          type={data.is_valid ? 'success' : 'error'}
          showIcon
        />
      )}

      {/* Issues by Severity */}
      {errors.length > 0 && (
        <div>
          <Title level={5}>
            <CloseCircleTwoTone twoToneColor="#ff4d4f" /> ERRORS ({errors.length})
          </Title>
          {errors.map((issue, idx) => (
            <ValidationIssueCard key={idx} issue={issue} />
          ))}
        </div>
      )}

      {warnings.length > 0 && (
        <div>
          <Title level={5}>
            <WarningOutlined style={{ color: '#faad14' }} /> WARNINGS ({warnings.length})
          </Title>
          {warnings.map((issue, idx) => (
            <ValidationIssueCard key={idx} issue={issue} />
          ))}
        </div>
      )}

      {infos.length > 0 && (
        <div>
          <Title level={5}>
            <InfoCircleOutlined style={{ color: '#1890ff' }} /> INFO ({infos.length})
          </Title>
          {infos.map((issue, idx) => (
            <ValidationIssueCard key={idx} issue={issue} />
          ))}
        </div>
      )}

      {/* No issues */}
      {issues.length === 0 && (
        <Alert
          message="No Issues Found"
          description="The execution plan passed all validation checks."
          type="success"
          showIcon
        />
      )}
    </Space>
  );
};

export default VerificationResultsPanel;
